import {
  Flex,
  IconSearchLine,
  ScreenReaderContent,
  SimpleSelect,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import { SubjectService } from "../../services/SubjectService";
import { UserService } from "../../services/UserService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import DepartmentCell from "./DepartmentCell";

const Users = () => {
  const showNotification = useNotification();
  const [users, setUsers] = useState([]);
  const [userCount, setUserCount] = useState();
  const [departments, setDepartments] = useState([]);

  const [rows, setRows] = useState([]);
  const [sortedRows, setSortedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sortColumns, setSortColumns] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState("");

  let lastSearch = undefined;

  const getRole = (roleId) => {
    switch (roleId) {
      case "ac":
        return t("user.assessmentCoordinator");
      case "pc":
        return t("user.programCoordinator");
      case "faculty":
        return t("user.faculty");
      default:
        return "";
    }
  };

  // Fetch users and departments
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        let [users, departments] = await Promise.all([
          UserService.getUsers(),
          SubjectService.getSubjects(),
        ]);

        setUserCount(users.length);
        setUsers(users);
        buildRows(users);

        let selectOptions = departments.map((s) => ({
          id: s.title,
          label: s.name,
        }));
        setDepartments(selectOptions);

        setLoading(false);
      } catch {
        showNotification(t("notifications.user.errorFetchingUsers"), ERROR);
        setLoading(false);
        setError(true);
      }
    };
    fetchInfo();
  }, [showNotification]);

  // Build rows for the DataGrid given a list of users
  const buildRows = useCallback((users) => {
    const newRows = users.map((user, index) => {
      const fullName = user.name.trim().split(" ");
      const firstName = fullName.length > 1 ? fullName[0] : "";
      const lastName =
        fullName.length > 1 ? fullName.splice(1).join(" ") : fullName[0];
      return {
        index: index,
        canvasId: user.canvas_id,
        fullName: user.name,
        fname: firstName,
        lname: lastName,
        email: user.login_id ?? "",
        role: getRole(user.role_id),
        roleId: user.role_id,
        departments: user.ac_subjects,
      };
    });
    setRows(newRows);
  }, []);

  // Set academic subjects to empty list if role is changed to an AC
  const resetDepartments = useCallback(
    (canvasId, index) => {
      UserService.setAcSubjects(canvasId, []).then(() => {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          newRows[index] = { ...newRows[index], departments: [] };
          return newRows;
        });
      });
    },
    [showNotification, users]
  );

  // Set user role
  const setUserRole = useCallback(
    (canvasId, index, roleId) => {
      UserService.setUserRole(canvasId, roleId)
        .then(() => {
          showNotification(t("notifications.user.roleUpdateSuccess"));
        })
        .catch((e) => {
          showNotification(
            `${t("notifications.user.roleUpdateError")} ${e}`,
            ERROR
          );
        });
      if (roleId === "ac") {
        resetDepartments(canvasId, index);
      }
    },
    [resetDepartments, showNotification, users]
  );

  const handleRoleSelect = (row, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[row.index] = {
        ...newRows[row.index],
        roleId: value,
        role: getRole(value),
      };
      return newRows;
    });
    setUserRole(row.canvasId, row.index, value);
  };

  // Set columns for the DataGrid
  useEffect(() => {
    setColumns([
      { key: "fname", name: t("user.firstName"), sortable: true },
      { key: "lname", name: t("user.lastName"), sortable: true },
      { key: "email", name: t("user.username"), sortable: true },
      {
        key: "role",
        name: t("user.role"),
        sortable: true,
        renderEditCell: ({ row }) => (
          <>
            <label htmlFor={"roleSelector" + row.canvasId}>
              <ScreenReaderContent>{t("user.role")}</ScreenReaderContent>
            </label>
            <SimpleSelect
              renderLabel=""
              autoFocus
              id={"roleSelector" + row.canvasId}
              value={row.roleId}
              onChange={(_e, { value }) => handleRoleSelect(row, value)}
            >
              <SimpleSelect.Option id={"ac"} value={"ac"}>
                {t("user.assessmentCoordinator")}
              </SimpleSelect.Option>
              <SimpleSelect.Option id={"pc"} value={"pc"}>
                {t("user.programCoordinator")}
              </SimpleSelect.Option>
              <SimpleSelect.Option id={"faculty"} value={"faculty"}>
                {t("user.faculty")}
              </SimpleSelect.Option>
            </SimpleSelect>
          </>
        ),
        editorOptions: { editOnClick: true },
      },
      {
        key: "departments",
        name: t("assessment.departments"),
        width: 150,
        renderCell: ({ row }) => (
          <DepartmentCell row={row} departments={departments} />
        ),
      },
    ]);
  }, [departments, setUserRole]);

  const getComparator = (columnKey) => {
    switch (columnKey) {
      case "fname":
      case "lname":
      case "email":
      case "role":
        return (a, b) => {
          return a[columnKey].localeCompare(b[columnKey]);
        };
      default:
    }
  };

  useEffect(() => {
    const filteredRows = rows.filter((row) => {
      return row.fullName.toLowerCase().includes(searchText.toLowerCase());
    });

    if (sortColumns.length === 0) setSortedRows(filteredRows);

    const newRows = filteredRows.toSorted((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
    setSortedRows(newRows);
  }, [rows, sortColumns, searchText]);

  // Handle search input change
  // Debounce the search input to avoid too many re-renders
  const handleChange = (_e, value) => {
    window.clearTimeout(lastSearch);
    lastSearch = window.setTimeout(() => {
      setSearchText(value);
    }, 500);
  };

  return !loading ? (
    !error && (
      <Flex wrap="wrap" gap="small" justifyItems="space-between">
        <Flex.Item>
          <Text>
            {t("user.totalUsers")}
            {userCount}
          </Text>
        </Flex.Item>
        <Flex.Item>
          <TextInput
            renderLabel={
              <ScreenReaderContent>{t("user.searchUsers")}</ScreenReaderContent>
            }
            placeholder={t("user.searchUsers")}
            size="small"
            onChange={handleChange}
            renderBeforeInput={<IconSearchLine inline={false} />}
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <DataGrid
            className="rdg-light"
            style={{
              overflow: "scroll",
              height: "auto",
              maxHeight: "600px",
              minHeight: "300px",
            }}
            rowHeight={55}
            headerRowHeight={40}
            columns={columns}
            rows={sortedRows}
            sortColumns={sortColumns}
            onSortColumnsChange={(cols) => setSortColumns(cols.slice(-1))}
            onCellClick={(args, event) => {
              if (args.column.key === "role") {
                event.preventGridDefault();
                args.selectCell(true);
              }
            }}
          />
        </Flex.Item>
      </Flex>
    )
  ) : (
    <Flex height="30rem" justifyItems="center">
      <Flex.Item textAlign="center">
        <Spinner renderTitle={t("user.loadingUserData")} size="large" />
        <Text weight="bold" size="large" as="div">
          {t("user.loadingUsers")}
        </Text>
      </Flex.Item>
    </Flex>
  );
};

export default Users;
